import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import PendingRequestsTable from '@/tables/PendingRequestsTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/pending-requests/',
)({
  component: PendingRequestsScreen,
});

function PendingRequestsScreen() {
  const { t } = useTranslation();

  const { groupName } = Route.useParams();
  const { setCreatedRequestId, setCanceledRequestId } = useFollowUpsAlert();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  // Create and cancel both land here, so this screen reads and clears their
  // alerts; legacy's notices last one page load.
  //
  // The clear is deferred and cancellable rather than inline in the cleanup:
  // StrictMode runs a fresh mount's effects twice in dev, and an inline clear
  // on that phantom unmount wiped the banner before it painted. The next
  // mount's effect cancels the pending clear, so only a real unmount clears it.
  const pendingClearRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => {
    if (pendingClearRef.current !== undefined) {
      clearTimeout(pendingClearRef.current);
      pendingClearRef.current = undefined;
    }
    return () => {
      pendingClearRef.current = setTimeout(() => {
        setCreatedRequestId(null);
        setCanceledRequestId(null);
      }, 0);
    };
  }, [setCreatedRequestId, setCanceledRequestId]);

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.pending-requests')}
          placement="follow_ups"
        />
      </GroupHeaderWrapper>
      <PendingRequestsTable
        groupId={groupData?.id ?? 0}
        groupName={groupData?.name ?? ''}
        groupSlug={groupName}
      />
    </>
  );
}
